import { FC, Fragment } from "preact/compat";
import { useEffect, useRef } from "preact/hooks";
import { Transition } from "@headlessui/react";
import { useSignal } from "@preact/signals";
import { format } from "date-fns";
import { useBreakpoint } from "~/hooks";
import { WheelTimeInput } from "../WheelInput";
import { SingleTimeProps, TimeSuggestion } from "./interfaces";
import { formatTime, isMinViolation, parseTimeString } from "./utils";
import cn from "classnames";

const SingleTime: FC<SingleTimeProps> = ({
    value,
    min,
    readOnly,
    disabled,
    suggestions,
    name,
    label,
    className,
    showDate,
    showYear,
    disabledLabel,
    onChange,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const bpMd = useBreakpoint("md");
    const textValue = useSignal(formatTime(value));
    const showSuggestions = useSignal(false);
    const isWheel = useSignal(false);
    const parsed = parseTimeString(textValue.value);
    const isInvalidString = parsed === null;
    const isMinInvalid = isMinViolation(parsed, min);
    const isInvalid = (isInvalidString || isMinInvalid) && !disabled;
    const isPopupVisible = showSuggestions.value && suggestions.length > 0;

    useEffect(() => {
        textValue.value = formatTime(value);
    }, [textValue, value]);

    const handleClick = () => {
        if (!bpMd.value) {
            isWheel.value = true;
            return;
        }

        if (disabled || readOnly) {
            return;
        }

        showSuggestions.value = true;
    };

    const handleSelectSuggestion = (item: TimeSuggestion) => {
        // assume that the suggestion is valid
        onChange(item.date);
    };

    const handleBlur = ({ relatedTarget }: FocusEvent) => {
        showSuggestions.value = false;

        if (
            relatedTarget instanceof HTMLElement &&
            relatedTarget.dataset.suggestion === "true"
        ) {
            return;
        }

        if (parsed && !isMinInvalid) {
            const newValue = new Date(value);

            newValue.setHours(parsed[0]);
            newValue.setMinutes(parsed[1]);
            newValue.setSeconds(0);

            onChange(newValue);
        } else {
            textValue.value = formatTime(value);
        }
    };

    const handleDragStart = (event: DragEvent) => {
        event.preventDefault();
    };

    const scrollToSuggestion = (value: string) => {
        const root = rootRef.current;

        if (!root || !isPopupVisible) {
            return;
        }

        const parsed = parseTimeString(value);

        if (!parsed) {
            return;
        }

        const item = root.querySelector<HTMLElement>(
            `[data-hour="${parsed[0]}"]`,
        );

        if (item) {
            item.scrollIntoView();
        }
    };

    return (
        <div ref={rootRef} className={cn("relative flex flex-col", className)}>
            <Transition
                as={Fragment}
                show={isInvalid}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 -translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-1"
            >
                <div
                    className={cn(
                        "pointer-events-none absolute left-0 mb-1 w-max select-none rounded bg-black/60 p-1 text-center text-xs text-white",
                        label ? "bottom-[calc(100%-20px)]" : "bottom-full",
                    )}
                >
                    {isMinInvalid ? "Must be after start" : "Invalid time"}
                </div>
            </Transition>
            {label && (
                <span className="pl-2 text-sm text-gray-500">{label}</span>
            )}
            <input
                type="text"
                placeholder="hh:mm"
                name={name}
                autoComplete="off"
                value={
                    disabled && disabledLabel ? disabledLabel : textValue.value
                }
                disabled={disabled}
                readOnly={readOnly || !bpMd.value}
                onInput={({ currentTarget }) => {
                    textValue.value = currentTarget.value;
                    scrollToSuggestion(currentTarget.value);
                }}
                onDragStart={handleDragStart}
                onClick={handleClick}
                onBlur={handleBlur}
                className={cn(
                    "w-[80px] rounded border-b-2 border-transparent p-2 outline-none transition duration-100 md:w-[100px]",
                    isInvalid &&
                        !disabled &&
                        !readOnly &&
                        "border-red-500 bg-red-100",
                    !isInvalid &&
                        !disabled &&
                        !readOnly &&
                        "hover:border-sky-500 focus:border-sky-500 focus:bg-gray-200",
                )}
            />
            {(showDate || showYear) && (
                <div className="mt-0.5 pl-2 text-xs text-gray-500">
                    {showYear
                        ? format(value, "dd MMM yyyy")
                        : format(value, "dd MMM")}
                </div>
            )}
            <Transition
                as={Fragment}
                show={isPopupVisible}
                enter="transition ease-out duration-100"
                enterFrom="opacity-0 -translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-75"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 -translate-y-1"
            >
                <div class="overflow-y-overlay absolute left-0 top-full z-50 mt-2 h-[120px] max-h-[120px] w-[180px] rounded border border-gray-200 bg-white shadow-lg">
                    {suggestions.map((suggestion) => (
                        <button
                            key={suggestion.label}
                            type="button"
                            tabIndex={-1}
                            data-suggestion="true"
                            data-hour={suggestion.date.getHours()}
                            className="block w-full px-4 py-1 text-left transition duration-100 hover:bg-gray-100"
                            onMouseDown={() =>
                                handleSelectSuggestion(suggestion)
                            }
                        >
                            {suggestion.label}
                        </button>
                    ))}
                </div>
            </Transition>
            <WheelTimeInput
                show={isWheel}
                value={value}
                onChange={onChange}
                min={min}
            />
        </div>
    );
};

export default SingleTime;
